import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * SRG Hero — split editorial layout.
 * Left: ivory ground with eyebrow, headline, lede, CTAs, strap.
 * Right: architectural navy panel with concentric gold arcs and the
 * "S" monogram — the brand "stand out" moment.
 */
export function Hero() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] min-h-[620px] bg-background">
      {/* COPY */}
      <div className="flex flex-col justify-center px-8 py-20 lg:py-0 lg:pr-16 lg:pl-8 max-w-[720px] lg:ml-auto w-full">
        <div className="flex items-center gap-3.5 mb-7">
          <span className="w-9 h-px bg-accent" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            An uncommon standard of perspective
          </span>
        </div>

        <h1 className="font-serif font-medium text-primary leading-[1.08] tracking-[-0.01em] text-4xl sm:text-5xl lg:text-[clamp(44px,5.2vw,66px)] mb-6">
          You've built the business.{" "}
          <em className="italic text-accent font-medium">
            Now think beyond it.
          </em>
        </h1>

        <p className="text-muted-foreground text-base lg:text-[17px] leading-relaxed max-w-[560px] mb-9">
          Growth, capital, continuity, and personal wealth are not separate
          decisions. We help business owners understand the connections—and
          address the consequences before they become problems.
        </p>

        <div className="flex flex-wrap items-center gap-7 mb-14">
          <Link
            to="/start-here"
            className="inline-flex items-center gap-3 bg-primary text-primary-foreground text-[11px] font-semibold uppercase tracking-[0.2em] px-7 py-4 hover:bg-secondary transition-colors"
          >
            Begin a Private Conversation <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/where-srg-fits"
            className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground pb-1.5 border-b border-border hover:border-accent transition-colors"
          >
            The SRG Philosophy <span>→</span>
          </Link>
        </div>

        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-t border-border pt-5">
          Thoughtful planning. Clear analysis. No unnecessary complexity.
        </div>
      </div>

      {/* PANEL */}
      <div className="relative bg-primary text-primary-foreground flex items-center justify-center overflow-hidden min-h-[440px] lg:min-h-[520px]">
        {/* Concentric gold arcs */}
        <svg
          viewBox="0 0 600 700"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
        >
          <g
            fill="none"
            stroke="hsl(var(--accent))"
            strokeOpacity="0.55"
            strokeWidth="1"
          >
            <path d="M60 700 V300 A240 240 0 0 1 540 300 V700" />
            <path
              d="M110 700 V320 A190 190 0 0 1 490 320 V700"
              strokeOpacity="0.35"
            />
            <path
              d="M160 700 V340 A140 140 0 0 1 440 340 V700"
              strokeOpacity="0.25"
            />
            <path
              d="M10 700 V280 A290 290 0 0 1 590 280 V700"
              strokeOpacity="0.18"
            />
          </g>
        </svg>

        <div className="relative text-center px-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-primary-foreground/75 mb-6">
            Strategy · Discipline · Continuity
          </div>
          <div className="font-serif text-[88px] lg:text-[120px] leading-none text-background">
            S
          </div>
          <div className="w-14 h-px bg-accent mx-auto my-6" />
          <div className="font-serif text-2xl lg:text-[30px] leading-tight text-background">
            Business is personal.
            <br />
            Strategy should be precise.
          </div>
        </div>

        <div className="absolute right-7 bottom-5 text-[9px] uppercase tracking-[0.24em] text-primary-foreground/60">
          The SRG perspective / Est. by experience
        </div>
      </div>
    </section>
  );
}
